import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { API } from '@/App';
import axios from 'axios';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';

const FeaturedBusinesses = () => {
  const [featuredAds, setFeaturedAds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    fetchFeaturedBusinesses();
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % Math.max(featuredAds.length, 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [featuredAds.length]);

  const fetchFeaturedBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/advertisements/active?ad_type=featured_business`);
      setFeaturedAds(response.data);
    } catch (error) {
      console.error('Failed to fetch featured businesses:', error);
    }
  };

  const handleAdClick = async (ad) => {
    try {
      await axios.post(`${API}/advertisements/${ad.id}/track`, { action: 'click' });
      if (ad.link_url) {
        window.open(ad.link_url, '_blank');
      }
    } catch (error) {
      console.error('Failed to track click:', error);
    }
  };

  const trackView = async (ad) => {
    try {
      await axios.post(`${API}/advertisements/${ad.id}/track`, { action: 'view' });
    } catch (error) {
      console.error('Failed to track view:', error);
    }
  };

  useEffect(() => {
    if (featuredAds[currentIndex]) {
      trackView(featuredAds[currentIndex]);
    }
  }, [currentIndex]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % featuredAds.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + featuredAds.length) % featuredAds.length);
  };

  if (featuredAds.length === 0) return null;

  const currentAd = featuredAds[currentIndex];

  return (
    <section className="container mx-auto px-4 py-12" data-testid="featured-businesses">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
          Featured Businesses
        </h2>
        <p className="text-gray-600">Discover businesses empowering women entrepreneurs</p>
      </div>

      <div className="max-w-4xl mx-auto relative">
        <Card className="glass overflow-hidden">
          <CardContent className="p-0">
            <div className="grid md:grid-cols-2">
              {currentAd.image_url && (
                <div className="h-64 md:h-auto">
                  <img 
                    src={currentAd.image_url} 
                    alt={currentAd.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="p-8 flex flex-col justify-center">
                <div className="text-xs px-3 py-1 bg-rose-100 text-rose-700 rounded-full w-fit mb-3">
                  Featured Partner
                </div>
                <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {currentAd.title}
                </h3>
                {currentAd.content && (
                  <p className="text-gray-700 mb-4">
                    {currentAd.content}
                  </p>
                )}
                {currentAd.link_url && (
                  <Button 
                    onClick={() => handleAdClick(currentAd)}
                    className="bg-rose-500 hover:bg-rose-600 w-fit"
                  >
                    Learn More <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {featuredAds.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg"
            >
              <ChevronLeft className="w-6 h-6 text-rose-600" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg"
            >
              <ChevronRight className="w-6 h-6 text-rose-600" />
            </button>

            <div className="flex justify-center gap-2 mt-4">
              {featuredAds.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex ? 'bg-rose-500 w-8' : 'bg-rose-200'
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default FeaturedBusinesses;